import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Power, Search, X, DollarSign, Calendar, TrendingUp } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminOffers() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLoanType, setFilterLoanType] = useState("all");
  const [filterActive, setFilterActive] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    loan_type: "personal_loan",
    min_amount: "",
    max_amount: "",
    interest_rate: "",
    tenure_months: "",
    min_income_required: "",
    active: true
  });

  useEffect(() => {
    if (!token || user?.role !== "super_admin") {
      navigate("/admin/login");
      return;
    }
    loadOffers();
  }, [token, user, navigate, searchTerm, filterLoanType, filterActive]);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterLoanType !== 'all') params.append('loan_type', filterLoanType);
      if (filterActive !== 'all') params.append('active', filterActive);
      
      const res = await axios.get(`${API_URL}/api/admin/offers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(res.data.offers || []);
    } catch (error) {
      toast.error("Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.min_amount || !formData.max_amount) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const payload = {
        ...formData,
        min_amount: parseFloat(formData.min_amount),
        max_amount: parseFloat(formData.max_amount),
        interest_rate: parseFloat(formData.interest_rate),
        tenure_months: parseInt(formData.tenure_months),
        min_income_required: formData.min_income_required ? parseFloat(formData.min_income_required) : null
      };

      if (editingOffer) {
        await axios.put(`${API_URL}/api/admin/offers/${editingOffer.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Offer updated successfully");
      } else {
        await axios.post(`${API_URL}/api/admin/offers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Offer created successfully");
      }
      
      setShowModal(false);
      setEditingOffer(null);
      resetForm();
      loadOffers();
    } catch (error) {
      toast.error("Failed to save offer");
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      title: offer.title,
      description: offer.description,
      loan_type: offer.loan_type,
      min_amount: offer.min_amount.toString(),
      max_amount: offer.max_amount.toString(),
      interest_rate: offer.interest_rate.toString(),
      tenure_months: offer.tenure_months.toString(),
      min_income_required: offer.min_income_required?.toString() || "",
      active: offer.active
    });
    setShowModal(true);
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/offers/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offer deleted successfully");
      loadOffers();
    } catch (error) {
      toast.error("Failed to delete offer");
    }
  };

  const handleToggleStatus = async (offerId) => {
    try {
      await axios.put(`${API_URL}/api/admin/offers/${offerId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated");
      loadOffers();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      loan_type: "personal_loan",
      min_amount: "",
      max_amount: "",
      interest_rate: "",
      tenure_months: "",
      min_income_required: "",
      active: true
    });
  };

  const loanTypeOptions = [
    { value: "personal_loan", label: "💵 Personal Loan", emoji: "💵" },
    { value: "home_loan", label: "🏠 Home Loan", emoji: "🏠" },
    { value: "car_loan", label: "🚗 Car Loan", emoji: "🚗" },
    { value: "education_loan", label: "🎓 Education Loan", emoji: "🎓" },
    { value: "business_loan", label: "💼 Business Loan", emoji: "💼" },
    { value: "credit_card", label: "💳 Credit Card EMI", emoji: "💳" },
    { value: "other", label: "📋 Other", emoji: "📋" }
  ];

  const getLoanTypeDisplay = (type) => {
    const option = loanTypeOptions.find(o => o.value === type);
    return option ? option.label : type;
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 50%, #00BCD4 100%)'}}>
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-cyan-400 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl animate-pulse-glow" style={{animationDelay: '1s'}}></div>
      </div>

      {/* Header */}
      <header className="glass-dark px-6 py-4 relative z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin/dashboard")} className="text-white/80 hover:text-white">
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}}>Manage Offers</h1>
          </div>
          <Button onClick={() => { resetForm(); setEditingOffer(null); setShowModal(true); }} className="btn-gradient">
            <Plus className="h-5 w-5 mr-2" /> Create Offer
          </Button>
        </div>
      </header>

      {/* Filters */}
      <div className="glass-dark px-6 py-4 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-white/60" />
              <Input
                placeholder="Search offers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60"
              />
            </div>
          </div>
          <select
            value={filterLoanType}
            onChange={(e) => setFilterLoanType(e.target.value)}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
          >
            <option value="all" className="text-slate-900">All Loan Types</option>
            {loanTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value} className="text-slate-900">{opt.label}</option>
            ))}
          </select>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
          >
            <option value="all" className="text-slate-900">All Status</option>
            <option value="true" className="text-slate-900">Active</option>
            <option value="false" className="text-slate-900">Inactive</option>
          </select>
        </div>
      </div>

      {/* Offers Grid */}
      <div className="flex-1 p-6 relative z-10 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="glass rounded-2xl p-12 text-center">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-white/70">Loading offers...</p>
            </div>
          ) : offers.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center">
              <DollarSign className="h-16 w-16 text-white/40 mx-auto mb-4" />
              <p className="text-xl font-semibold text-white mb-2">No Offers Found</p>
              <p className="text-white/70 mb-6">Create your first EMI assistance offer</p>
              <Button onClick={() => setShowModal(true)} className="btn-gradient">
                <Plus className="h-5 w-5 mr-2" /> Create First Offer
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.map((offer) => (
                <div key={offer.id} className="glass rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 animate-fade-in">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      offer.active ? 'bg-green-500/20 text-green-300' : 'bg-slate-500/20 text-slate-300'
                    }`}>
                      {offer.active ? '✓ Active' : '○ Inactive'}
                    </span>
                    <span className="text-sm text-white/60">{getLoanTypeDisplay(offer.loan_type).split(' ')[0]}</span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-2">{offer.title}</h3>
                  <p className="text-sm text-white/70 mb-4 line-clamp-2">{offer.description}</p>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-xs text-white/60 mb-1">Loan Amount</div>
                      <div className="text-sm font-semibold text-white">₹{(offer.min_amount/100000).toFixed(1)}L - ₹{(offer.max_amount/100000).toFixed(1)}L</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-xs text-white/60 mb-1">Interest Rate</div>
                      <div className="text-sm font-semibold text-cyan-300">{offer.interest_rate}% p.a.</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-xs text-white/60 mb-1">Tenure</div>
                      <div className="text-sm font-semibold text-white">{offer.tenure_months} months</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-xs text-white/60 mb-1">Min Income</div>
                      <div className="text-sm font-semibold text-white">
                        {offer.min_income_required ? `₹${(offer.min_income_required/1000).toFixed(0)}K` : 'Any'}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(offer)}
                      className="flex-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit2 className="h-4 w-4" /> Edit
                    </button>
                    <button
                      onClick={() => handleToggleStatus(offer.id)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                        offer.active
                          ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300'
                          : 'bg-green-500/20 hover:bg-green-500/30 text-green-300'
                      }`}
                    >
                      <Power className="h-4 w-4" /> {offer.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(offer.id)}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-300 py-2 px-3 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="glass rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{editingOffer ? 'Edit Offer' : 'Create New Offer'}</h2>
              <button onClick={() => { setShowModal(false); setEditingOffer(null); resetForm(); }} className="text-white/60 hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-white/90 font-semibold">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Low Interest Personal Loan"
                  className="mt-2 bg-white/10 border-white/20 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-white/90 font-semibold">Description</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe the offer benefits..."
                  className="mt-2 w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 min-h-[100px]"
                />
              </div>

              <div>
                <Label className="text-white/90 font-semibold">Loan Type *</Label>
                <select
                  value={formData.loan_type}
                  onChange={(e) => setFormData({...formData, loan_type: e.target.value})}
                  className="mt-2 w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
                  required
                >
                  {loanTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value} className="text-slate-900">{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/90 font-semibold">Min Amount (₹) *</Label>
                  <Input
                    type="number"
                    value={formData.min_amount}
                    onChange={(e) => setFormData({...formData, min_amount: e.target.value})}
                    placeholder="50000"
                    className="mt-2 bg-white/10 border-white/20 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-white/90 font-semibold">Max Amount (₹) *</Label>
                  <Input
                    type="number"
                    value={formData.max_amount}
                    onChange={(e) => setFormData({...formData, max_amount: e.target.value})}
                    placeholder="500000"
                    className="mt-2 bg-white/10 border-white/20 text-white"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/90 font-semibold">Interest Rate (% p.a.) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({...formData, interest_rate: e.target.value})}
                    placeholder="12.5"
                    className="mt-2 bg-white/10 border-white/20 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-white/90 font-semibold">Tenure (months) *</Label>
                  <Input
                    type="number"
                    value={formData.tenure_months}
                    onChange={(e) => setFormData({...formData, tenure_months: e.target.value})}
                    placeholder="24"
                    className="mt-2 bg-white/10 border-white/20 text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-white/90 font-semibold">Minimum Monthly Income (₹)</Label>
                <Input
                  type="number"
                  value={formData.min_income_required}
                  onChange={(e) => setFormData({...formData, min_income_required: e.target.value})}
                  placeholder="25000 (optional)"
                  className="mt-2 bg-white/10 border-white/20 text-white"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  className="w-5 h-5"
                />
                <Label htmlFor="active" className="text-white/90 font-semibold cursor-pointer">Active (visible to customers)</Label>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 btn-gradient h-12 rounded-xl font-semibold">
                  {editingOffer ? 'Update Offer' : 'Create Offer'}
                </button>
                <button type="button" onClick={() => { setShowModal(false); setEditingOffer(null); resetForm(); }} className="px-6 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
